import { API } from '@aws-amplify/api';
import { PubSub } from '@aws-amplify/pubsub';
import { Refresh } from '@material-ui/icons';
import { TextField, Box } from '@material-ui/core';
import { useState, useEffect, useRef } from 'react';
import { makeStyles } from '@material-ui/core/styles';
import { EmiconShadow, MqttResponseEmicon } from '../../types/ShadowGraphic';
import { startOfYear, endOfYear, differenceInDays, isSameYear } from 'date-fns';
import { MuiPickersUtilsProvider, KeyboardDatePicker } from '@material-ui/pickers';
import Chart from './Chart';
import Grid from '@material-ui/core/Grid';
import useStateRef from 'react-usestateref';
import ShadowGraphic from './ShadowGraphic';
import DateFnsUtils from '@date-io/date-fns';
import Button from '@material-ui/core/Button';
import Skeleton from '@material-ui/lab/Skeleton';
import Collapse from '@material-ui/core/Collapse';
import Typography from '@material-ui/core/Typography';
import ToggleButton from '@material-ui/lab/ToggleButton';
import ToggleButtonGroup from '@material-ui/lab/ToggleButtonGroup';
import EmiconBerichtDeckblattInfo from './EmiconBerichtDeckblattInfo';
import LoaderButton from '../LoaderButton';

const useStyles = makeStyles((theme) => ({
  chartSkeleton: {
    marginBottom: 24,
    marginTop: 24,
  },
  toggle: {
    padding: '6px 16px',
  },
  toggleContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginTop: 8,
    marginBottom: 8,
  },
  toggleGroup: {
    paddingLeft: theme.spacing(3),
    paddingRight: theme.spacing(3),
  },

  noData: {
    padding: 36,
  },

  fetchLiveDataButton: {
    animationName: '$loadingBlink',
    animationDuration: '2s',
    animationTimingFunction: 'ease-out',
    animationIterationCount: 'infinite',
  },
}));

interface ChartDropdownProps {
  serialNumber: string
  showChart: boolean
  isEmicon?: boolean
  deckblattInfo: DeckblattInfo
  fetchDevices: Function
}

export function ChartDropdown({
  serialNumber, showChart, isEmicon = false, deckblattInfo, fetchDevices
}: ChartDropdownProps) {
  const classes = useStyles();
  const [valueMode, setValueMode] = useState('hmw');
  const [days, setDays] = useState(30);
  const [finalDays, setFinalDays] = useState(30);
  const [loading, setLoading] = useState(false);
  const [noData, setNoData] = useState(false);
  const [noDataTermine, setNoDataTermine] = useState(false);
  const [chartMode, setChartMode] = useState('all');
  const [selectedYear, setSelectedYear] = useState<Date | null>(new Date());
  const [chartMaxDate, setChartMaxDate] = useState<number>(Date.now());
  const [searchMode, setSearchMode] = useState<'days' | 'year'>('days');

  const [chartData, setChartData] = useState({
    coTMW: [],
    coHMW: [],
    noxTMW: [],
    noxHMW: [],
    tempTMW: [],
    tempHMW: [],
    noxAlarm: [],
    coAlarm: [],
    tempAlarm: [],
    tempMin: [],
    tempMax: [],
  });

  const [chartDataTermine, setChartDataTermine] = useState({
    allEvents: [],
  });

  const buildParams = (customParams?: { amountOfDays?: number; start?: number; end?: number }) => {
    const params: any = { serialNumber, isEmicon };

    if (customParams?.start && customParams?.end) {
      params.start = customParams.start;
      params.end = customParams.end;
    } else {
      params.amountOfDays = customParams?.amountOfDays || days;
    }

    return params;
  };

  const fetchChartData = async (params: any) => {
    try {
      setNoData(false);
      const res = await API.get('emilog', 'chartData', { queryStringParameters: params });
      setChartData(res);
    } catch (err) {
      console.error('chartData error:', err);
      setNoData(true);
    }
  };

  const fetchChartDataTermine = async (params: any) => {
    try {
      setNoDataTermine(false);
      const res = await API.get('emilog', 'chartDataTermine', { queryStringParameters: params });
      setChartDataTermine(res);
    } catch (err) {
      console.error('chartDataTermine error:', err);
      setNoDataTermine(true);
    }
  };

  const fetchData = async (appliedDays: number, customParams?: { amountOfDays?: number; start?: number; end?: number }) => {
    if (!serialNumber) return;

    setLoading(true);

    const params = buildParams(customParams);

    await Promise.all([
      fetchChartData(params),
      fetchChartDataTermine(params)
    ]);

    // ✅ use explicit value instead of stale state
    if (appliedDays !== undefined) {
      setFinalDays(appliedDays);
    }

    setLoading(false);
  };

  const handleManualSearch = () => {
    setLoading(true);
    if (searchMode === 'year' && selectedYear) {
      // This handles the transition between CET and CEST (Summertime) automatically
      const start = startOfYear(selectedYear);
      let end = endOfYear(selectedYear);
      const today = new Date();

      // Check if the selected year is the current year
      if (isSameYear(selectedYear, today)) {
        // If it's this year, only go up to "now" to avoid empty space
        end = today;
      }
      // Calculate actual days in that specific year (365 or 366)
      const daysInYear = differenceInDays(end, start) + 1;
      setDays(daysInYear); // Update the state so the Chart knows the scale
      setChartMaxDate(end.getTime()); // Set chart boundary to Dec 31st of that year

      fetchData(daysInYear, {
        start: Math.floor(start.getTime() / 1000),
        end: Math.floor(end.getTime() / 1000)
      });
    } else {
      setChartMaxDate(Date.now()); // Set chart boundary to Dec 31st of that year
      fetchData(days, { amountOfDays: days });
    }
  };

  useEffect(() => {
    if (showChart) {
      fetchData(days, { amountOfDays: days });
    }
  }, [isEmicon, serialNumber, showChart]);

  function handleChartToggle(event: any, newValue: string) {
    event.preventDefault();
    setChartMode(newValue);
  }

  function handleValueToggle(event: any, newValue: string) {
    event.preventDefault();
    setValueMode(newValue);
  }

  const renderInputs = () => (
    <MuiPickersUtilsProvider utils={DateFnsUtils}>
      <Box display="flex" alignItems="center" gridGap={16} bgcolor="white" p={1} borderRadius={4}>
        {/* Mode Toggle */}
        <ToggleButtonGroup
          size="small"
          value={searchMode}
          exclusive
          onChange={(_, val) => val && setSearchMode(val)}
        >
          <ToggleButton value="days">Tage</ToggleButton>
          <ToggleButton value="year">Jahr</ToggleButton>
        </ToggleButtonGroup>

        {searchMode === 'days' ? (
          <TextField
            label="Anzahl Tage"
            type="number"
            value={days}
            variant="outlined"
            size="small"
            onChange={(e) => setDays(Number(e.target.value))}
            style={{ width: 140 }}
          />
        ) : (
          <KeyboardDatePicker
            views={["year"]}
            label="Jahr"
            inputVariant="outlined"
            size="small"
            value={selectedYear}
            onChange={(date) => setSelectedYear(date)}
            style={{ width: 140 }}
          />
        )}

        <LoaderButton
          isLoading={loading}            // Your new loading state
          text="Suchen"                  // The button label
          onClick={handleManualSearch}   // The search function
          variant="contained"            // Styling
          noMargin                       // To keep it aligned in your flex box
        />
      </Box>
    </MuiPickersUtilsProvider>
  );

  const renderToggle = () => (
    <ToggleButtonGroup
      exclusive
      value={chartMode}
      onChange={handleChartToggle}
      className={classes.toggleGroup}
    >
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="all"
      >
        Übersicht TMW
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="co"
      >
        CO
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="nox"
      >
        NOx
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="temp"
      >
        Temperatur
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="termine"
      >
        Termine
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="info"
      >
        Deckblatt Konfiguration
      </ToggleButton>
    </ToggleButtonGroup>
  );

  const renderValueToggle = () => (
    <ToggleButtonGroup
      exclusive
      value={valueMode}
      onChange={handleValueToggle}
      className={classes.toggleGroup}
    >
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="hmw"
      >
        HMW
      </ToggleButton>
      <ToggleButton
        classes={{ root: classes.toggle }}
        value="tmw"
      >
        TMW
      </ToggleButton>
    </ToggleButtonGroup>
  );

  /* Ab hier Livewerte */

  const subLive = useRef<MqttSubscription | null>(null);
  const subGet = useRef<MqttSubscription | null>(null);

  const [loadingLiveData, setLoadingLiveData, loadingLiveDataRef] = useStateRef(false);
  const [reported, setReported] = useState<Partial<EmiconShadow>>({});
  const [reportedTimestamp, setReportedTimestamp] = useState<number | null>(null);
  const [loaded, setLoaded] = useState<boolean>(false);

  function checkShadowValue(shadow: any, key1: string, key2: string, defaultValue: undefined | 'NaN') {
    if (Object.hasOwn(shadow, key1)) {
      shadow[key1] = shadow[key1];
    } else if (Object.hasOwn(shadow, key2)) {
      shadow[key1] = shadow[key2];
    } else {
      shadow[key1] = defaultValue;
    }
  }

  async function fetchShadow() {
    if (serialNumber && isEmicon) {
      // console.log("Fetching Shadow From:")
      // console.log({ serialNumber })
      try {
        const res = await API.get('emilog', 'getShadow', {
          queryStringParameters: {
            isEmicon: true,
            serialNumber,
          },
        }) as MqttResponseEmicon;
        // console.log({ serialNumber })
        // console.log({ res })
        setLoaded(true); // Shadowgraphic wurde geladen
        if (res.state.reported !== undefined && res.state.reported !== null) {
          checkShadowValue(res.state.reported, 'status', 'sStatus', 'NaN');
          checkShadowValue(res.state.reported, 'Var_RelO2', 'o2_bezug', undefined);
          checkShadowValue(res.state.reported, 'sSeriennummer', 'seriennummer', 'NaN'); // not sure about this one...
          checkShadowValue(res.state.reported, 'sVersion', 'version', 'NaN');
        }
        setReported((prevState: Partial<EmiconShadow>) => ({
          ...prevState,
          ...res.state.reported,
        }));
        setReportedTimestamp(
          res.metadata.reported?.status?.timestamp ??
          res.metadata.reported?.sStatus?.timestamp ??
          0
        );
      } catch (error: any) {
        console.log(error)
      }
    }
  }

  async function requestLiveValues() {
    if (serialNumber && isEmicon) {
      // if (serialNumber === 'EPSCR0000000002') console.log("EPSCR0000000002 hat requestet")
      PubSub.publish(`EMI-CON_${serialNumber}/EMI-CON/AWSDaten`, true);
      PubSub.publish(`$aws/things/EMI-CON_${serialNumber}/shadow/get`, {});
    }
  }

  function mapMqttResponse(value: MqttResponseEmicon) {
    if (value.state.reported !== undefined && value.state.reported !== null) {
      checkShadowValue(value.state.reported, 'status', 'sStatus', 'NaN');
      checkShadowValue(value.state.reported, 'Var_RelO2', 'o2_bezug', undefined);
      checkShadowValue(value.state.reported, 'sSeriennummer', 'seriennummer', 'NaN'); // not sure about this one...
      checkShadowValue(value.state.reported, 'sVersion', 'version', 'NaN');
    }
    // console.log("ShadowGraphic Live")
    // console.log({ value })
    setReported((prevState: Partial<EmiconShadow>) => ({
      ...prevState,
      ...value.state.reported,
    }));
    setReportedTimestamp(
      value.metadata.reported?.status?.timestamp ??
      value.metadata.reported?.sStatus?.timestamp ??
      0
    );
  }

  useEffect(() => {
    if (subLive.current) subLive.current!.unsubscribe();
    if (subGet.current) subGet.current!.unsubscribe();
    setReported({});
    if (serialNumber) {
      setLoading(true);
      fetchShadow();
      subLive.current = PubSub.subscribe(`$aws/things/EMI-CON_${serialNumber}/shadow/update/accepted`).subscribe({
        next: ({ value }) => {
          mapMqttResponse(value);
        },
        error: ((err) => {
          console.log(err);
        }),
        complete: () => {
          console.log('Subscription completed Update');
        },
      });
    }
    return function cleanUp() {
      if (subLive.current) subLive.current.unsubscribe();
      if (subGet.current) subGet.current.unsubscribe();
    };
  }, [serialNumber]);

  return (
    <Collapse in={showChart} timeout={200} unmountOnExit>
      <>
        <div className={classes.toggleContainer}>
          {isEmicon && chartMode !== 'info' ? (
            <Button
              disabled={loadingLiveData}
              onClick={requestLiveValues}
              className={loadingLiveData ? classes.fetchLiveDataButton : undefined}
              color="primary"
              variant="outlined"
              style={{ marginLeft: 16 }}
              startIcon={<Refresh />}
            >
              Update
            </Button>
          ) : (
            null
          )}
          {renderToggle()}
          {/* {renderDaySelect()} */}
          {chartMode !== 'info' ? renderInputs() : (null)}
          {chartMode !== 'all' && chartMode !== 'info' && chartMode !== 'termine' ? renderValueToggle() : (
            <div style={{
              width: 184,
            }}
            />
          )}
        </div>
        {!loading ? (
          <>
            {
              chartMode === 'info' ? (
                <EmiconBerichtDeckblattInfo
                  serialNumber={serialNumber}
                  isEmicon={isEmicon}
                  deckblattInfoFromDevice={deckblattInfo}
                  fetchDevices={fetchDevices}
                />
              ) : chartMode === 'termine' ? (
                noDataTermine ? (
                  <Typography className={classes.noData} component="h6">
                    Keine Termine im ausgewählten Zeitraum verfügbar
                  </Typography>
                ) : (
                  <Chart
                    isEventChart
                    type="events"
                    chartData={chartDataTermine?.allEvents || []}
                    days={finalDays}
                    maxDate={chartMaxDate}
                    fullSize
                  />
                )
              ) : noData ? (
                <Typography className={classes.noData} component="h6">
                  Keine Messdaten im angegeben Zeitraum aufgezeichnet
                </Typography>
              ) : chartMode === 'all' ? (
                <Grid container spacing={2} alignItems="center">
                  {isEmicon && (
                    <Grid item xs={12} lg={4}>
                      <ShadowGraphic
                        serialNumber={serialNumber}
                        isEmicon={isEmicon}
                        reported={reported}
                        loaded={loaded}
                        reportedTimestamp={reportedTimestamp}
                      />
                    </Grid>
                  )}

                  {!isEmicon && (
                    <Grid item xs={12} lg={4}>
                      <Chart
                        days={finalDays}
                        type="co"
                        chartData={chartData.coTMW}
                        limits={chartData.coAlarm}
                      />
                    </Grid>
                  )}

                  <Grid item xs={12} lg={4}>
                    <Chart
                      days={finalDays}
                      type="nox"
                      chartData={chartData.noxTMW}
                      limits={chartData.noxAlarm}
                    />
                  </Grid>

                  <Grid item xs={12} lg={4}>
                    <Chart
                      days={finalDays}
                      type="temp"
                      chartData={chartData.tempTMW}
                      limits={chartData.tempAlarm}
                    />
                  </Grid>
                </Grid>
              ) : chartMode === 'co' ? (
                <Chart
                  showHmw={valueMode === 'hmw'}
                  days={finalDays}
                  type="co"
                  chartData={valueMode === 'hmw' ? chartData.coHMW : chartData.coTMW}
                  limits={chartData.coAlarm}
                  fullSize
                />
              ) : chartMode === 'nox' ? (
                <Chart
                  showHmw={valueMode === 'hmw'}
                  days={finalDays}
                  type="nox"
                  chartData={valueMode === 'hmw' ? chartData.noxHMW : chartData.noxTMW}
                  limits={chartData.noxAlarm}
                  fullSize
                />
              ) : chartMode === 'temp' ? (
                <Chart
                  showHmw={valueMode === 'hmw'}
                  days={finalDays}
                  type="temp"
                  chartData={valueMode === 'hmw' ? chartData.tempHMW : chartData.tempTMW}
                  limits={chartData.tempAlarm}
                  fullSize
                />
              ) : null
            }
          </>
        ) : (
          <Grid container spacing={4} alignItems="center">
            {chartMode !== 'all' ? (
              <Grid item lg={12}>
                <Skeleton variant="rect" height={450} className={classes.chartSkeleton} />
              </Grid>
            ) : (
              <>

                {isEmicon ? (
                  <Grid item lg={4}>
                    <Skeleton variant="rect" height={450} className={classes.chartSkeleton} />
                  </Grid>
                ) : (
                  null
                )
                }

                {!isEmicon ? (
                  <Grid item lg={4}>
                    <Skeleton variant="rect" height={450} className={classes.chartSkeleton} />
                  </Grid>
                ) : (
                  null
                )
                }

                <Grid item lg={4}>
                  <Skeleton variant="rect" height={450} className={classes.chartSkeleton} />
                </Grid>

                <Grid item lg={4}>
                  <Skeleton variant="rect" height={450} className={classes.chartSkeleton} />
                </Grid>
              </>
            )}
          </Grid>
        )}
      </>

    </Collapse >
  );
}
